import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { CallPeer, fetchIceServers, getUserStream } from "../lib/webrtc";
import { useAuth } from "../context/AuthContext";
import { wsUrl, api } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Mic, MicOff, Video as VideoIcon, VideoOff, PhoneOff, Phone, X, WifiOff, DoorOpen,
} from "lucide-react";
import { toast } from "sonner";

const NO_ANSWER_MS = 45000;         // 45s ringing → auto-timeout (matches backend watcher)
const ICE_DISCONNECT_GRACE_MS = 2500; // wait before firing an ICE restart

/**
 * Call screen shared by kiosk (caller) and resident (callee).
 *
 * IMPORTANT architectural note (Phase 3): the callee does NOT open its own
 * authenticated WebSocket. It piggy-backs on the persistent socket held by
 * `GlobalWs` (accessible as `window.__fastviWs`). Opening a second WS for
 * the same user_id caused the ConnectionManager to close the "old" socket,
 * kicking off a reconnect loop between the two components. Only the kiosk
 * (unauth `/api/ws-kiosk/{call_id}`) opens its own socket, and even that
 * uses a stale-WS guard so React StrictMode double-mounts don't loop.
 */
export default function CallScreen() {
  const { callId } = useParams();
  const [params] = useSearchParams();
  const role = params.get("role") || "callee"; // "caller" | "callee"
  const visitor = params.get("visitor") || "Visitor";
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [status, setStatus] = useState(role === "caller" ? "ringing" : "incoming");
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [iceState, setIceState] = useState("new");
  const [connState, setConnState] = useState("new");
  const [notice, setNotice] = useState(null);
  const [doorUnlocked, setDoorUnlocked] = useState(false);

  const localRef = useRef(null);
  const remoteRef = useRef(null);

  const wsRef = useRef(null);            // only used for role=caller
  const peerRef = useRef(null);
  const statusRef = useRef(status);
  const remoteUserIdRef = useRef(null);
  const iceServersRef = useRef(null);
  const pendingSignalsRef = useRef([]);
  const noAnswerTimerRef = useRef(null);
  const iceRestartTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const teardownRef = useRef(false);
  const restartAttemptsRef = useRef(0);

  useEffect(() => { statusRef.current = status; }, [status]);

  // ---------------------------------------------------------------- helpers
  function setStatusSafe(next) {
    setStatus(next);
    statusRef.current = next;
  }

  function getResidentSocket() {
    return window.__fastviWs;
  }

  function sendSignal(target, payload) {
    if (!target) return false;
    const ws = role === "caller" ? wsRef.current : getResidentSocket();
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify({ to: target, ...payload }));
    return true;
  }

  function flushPendingSignals() {
    const peer = peerRef.current;
    if (!peer) return;
    const pending = pendingSignalsRef.current;
    pendingSignalsRef.current = [];
    pending.forEach((msg) => { peer.handleSignal(msg).catch(() => {}); });
  }

  async function ensureIceServers() {
    if (iceServersRef.current) return iceServersRef.current;
    const servers = await fetchIceServers({
      callId,
      isPublic: role === "caller",
    });
    iceServersRef.current = servers;
    return servers;
  }

  async function attachLocal() {
    try {
      if (localRef.current?.srcObject) return localRef.current.srcObject;
      const stream = await getUserStream({ video: true, audio: true });
      if (localRef.current) localRef.current.srcObject = stream;
      return stream;
    } catch (e) {
      toast.error("Camera/Mic permission required");
      throw e;
    }
  }

  async function makePeer({ asCaller, target }) {
    const iceServers = await ensureIceServers();
    const peer = new CallPeer({
      isCaller: asCaller,
      iceServers,
      signal: (msg) => sendSignal(target, msg),
      onRemoteStream: (stream) => {
        if (remoteRef.current) remoteRef.current.srcObject = stream;
        setNotice((n) => (n === null ? null : n));
      },
      onIceState: (s) => {
        setIceState(s);
        if (s === "disconnected") {
          if (iceRestartTimerRef.current) clearTimeout(iceRestartTimerRef.current);
          iceRestartTimerRef.current = setTimeout(() => {
            if (statusRef.current === "connected" && peerRef.current === peer) {
              peer.restartIce();
              setNotice("Reconnecting…");
            }
          }, ICE_DISCONNECT_GRACE_MS);
        } else if (s === "failed") {
          if (restartAttemptsRef.current < 2) {
            restartAttemptsRef.current += 1;
            peer.restartIce();
            setNotice("Reconnecting…");
          } else {
            setNotice("Call lost. Ending…");
            setStatusSafe("ended");
            cleanup();
            setTimeout(() => navigate(role === "callee" ? "/resident" : "/kiosk", { replace: true }), 1500);
          }
        } else if (s === "connected" || s === "completed") {
          setNotice(null);
        }
      },
      onConnectionState: (s) => setConnState(s),
    });
    peerRef.current = peer;

    const stream = await attachLocal();
    await peer.attachLocalStream(stream);

    if (asCaller) await peer.createOffer();
    flushPendingSignals();
    return peer;
  }

  function clearNoAnswerTimer() {
    if (noAnswerTimerRef.current) {
      clearTimeout(noAnswerTimerRef.current);
      noAnswerTimerRef.current = null;
    }
  }

  function armNoAnswerTimer() {
    clearNoAnswerTimer();
    noAnswerTimerRef.current = setTimeout(() => {
      if (statusRef.current === "ringing") {
        setStatusSafe("no_answer");
        setNotice("No answer");
        cleanup();
        setTimeout(() => navigate("/kiosk", { replace: true }), 1800);
      }
    }, NO_ANSWER_MS);
  }

  // ---------- unified message handler used by BOTH roles ------------------
  async function onSignal(msg) {
    if (msg.type === "call_accept") {
      clearNoAnswerTimer();
      remoteUserIdRef.current = msg.from;
      setStatusSafe("connected");
      await makePeer({ asCaller: true, target: msg.from });
      return;
    }
    if (msg.type === "call_reject") {
      setStatusSafe("rejected");
      setNotice(msg.reason === "busy" ? "Resident is busy" : "Call declined");
      cleanup();
      setTimeout(() => navigate(role === "callee" ? "/resident" : "/kiosk", { replace: true }), 1500);
      return;
    }
    if (msg.type === "call_end") {
      setStatusSafe("ended");
      cleanup();
      setTimeout(() => navigate(role === "callee" ? "/resident" : "/kiosk", { replace: true }), 1200);
      return;
    }
    if (msg.type === "door_unlock") {
      // Kiosk-side visual confirmation. The activity row + resident-side
      // toast are handled by the backend / resident code path respectively.
      setDoorUnlocked(true);
      setNotice(`Door unlocked${msg.resident_name ? " by " + msg.resident_name : ""}`);
      setTimeout(() => setDoorUnlocked(false), 5000);
      return;
    }
    if (msg.type === "call_status") {
      if (msg.status === "rejected") {
        setStatusSafe("rejected");
        setNotice(msg.reason === "busy" ? "Resident is busy" : "Call declined");
        cleanup();
        setTimeout(() => navigate("/kiosk", { replace: true }), 1500);
      } else if (msg.status === "no_answer") {
        setStatusSafe("no_answer");
        setNotice("No answer");
        cleanup();
        setTimeout(() => navigate("/kiosk", { replace: true }), 1800);
      } else if (msg.status === "ended") {
        setStatusSafe("ended");
        cleanup();
        setTimeout(() => navigate("/kiosk", { replace: true }), 1200);
      }
      return;
    }
    if (msg.type === "offer" || msg.type === "answer" || msg.type === "ice") {
      if (peerRef.current) {
        await peerRef.current.handleSignal(msg);
      } else {
        // Buffer until peer exists (offer can arrive before Accept has run
        // through, or during a mid-call ICE restart negotiation).
        pendingSignalsRef.current.push(msg);
      }
    }
  }

  // ---------------------------------------------- WebSocket wiring (caller)
  useEffect(() => {
    if (role !== "caller") return undefined;
    teardownRef.current = false;

    function open() {
      if (teardownRef.current) return;
      const url = wsUrl(`/api/ws-kiosk/${callId}`);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        restartAttemptsRef.current = 0;
        setNotice(null);
        armNoAnswerTimer();
      };
      ws.onmessage = async (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch { return; }
        await onSignal(msg);
      };
      ws.onclose = () => {
        // Stale-WS guard: React StrictMode double-mount + our own cleanup can
        // fire onclose for a socket we've already replaced. Ignore it.
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (teardownRef.current) return;
        const s = statusRef.current;
        if (s === "ringing" || s === "incoming" || s === "connected") {
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(open, 1500);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    }
    open();

    return () => {
      teardownRef.current = true;
      clearNoAnswerTimer();
      if (iceRestartTimerRef.current) clearTimeout(iceRestartTimerRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } }
    };
  }, [role, callId]); // eslint-disable-line

  // ---------------------------------------------- signaling wiring (callee)
  useEffect(() => {
    if (role !== "callee") return undefined;
    if (authLoading) return undefined;
    if (!user || !user.access_token) {
      toast.error("You need to sign in first");
      navigate("/login", { replace: true });
      return undefined;
    }
    // Hook into the persistent GlobalWs socket. It stays up across nav.
    const prev = window.__fastviSignalHandler;
    window.__fastviSignalHandler = (msg) => { onSignal(msg).catch(() => {}); };
    return () => {
      window.__fastviSignalHandler = prev || null;
    };
  }, [role, authLoading, user?.id, user?.access_token]); // eslint-disable-line

  // Full teardown on unmount (belt-and-braces).
  useEffect(() => {
    return () => {
      teardownRef.current = true;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Caller: start camera immediately for local preview while ringing.
  useEffect(() => {
    if (role === "caller") {
      attachLocal().catch(() => { /* handled */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  function cleanup() {
    clearNoAnswerTimer();
    if (iceRestartTimerRef.current) { clearTimeout(iceRestartTimerRef.current); iceRestartTimerRef.current = null; }
    if (peerRef.current) { peerRef.current.destroy(); peerRef.current = null; }
    if (localRef.current?.srcObject) {
      try { localRef.current.srcObject.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      localRef.current.srcObject = null;
    }
    if (remoteRef.current?.srcObject) {
      remoteRef.current.srcObject = null;
    }
  }

  async function waitForResidentSocket(timeoutMs = 3000) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const ws = getResidentSocket();
      if (ws && ws.readyState === WebSocket.OPEN) return true;
      await new Promise((r) => setTimeout(r, 60));
    }
    return false;
  }

  async function accept() {
    try {
      await api.post("/calls/action", { call_id: callId, action: "accept" });
      const ready = await waitForResidentSocket(3000);
      if (!ready) toast("Signaling channel not ready yet — retrying");
      sendSignal(`kiosk:${callId}`, { type: "call_accept" });
      await makePeer({ asCaller: false, target: `kiosk:${callId}` });
      setStatusSafe("connected");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Accept failed");
    }
  }

  async function reject() {
    try {
      if (role === "callee") {
        await api.post("/calls/action", { call_id: callId, action: "reject" });
      }
      sendSignal(role === "callee" ? `kiosk:${callId}` : "", { type: "call_reject" });
    } catch { /* noop */ }
    setStatusSafe("rejected");
    cleanup();
    setTimeout(() => navigate(role === "callee" ? "/resident" : "/kiosk", { replace: true }), 1200);
  }

  async function end() {
    try {
      if (role === "callee") {
        await api.post("/calls/action", { call_id: callId, action: "end" });
      }
      const target = role === "callee" ? `kiosk:${callId}` : remoteUserIdRef.current;
      if (target) sendSignal(target, { type: "call_end" });
    } catch { /* noop */ }
    setStatusSafe("ended");
    cleanup();
    setTimeout(() => navigate(role === "callee" ? "/resident" : "/kiosk", { replace: true }), 1200);
  }

  async function unlockDoor() {
    try {
      const { data } = await api.post(`/calls/${callId}/unlock`);
      setDoorUnlocked(true);
      const dstatus = data?.delivery?.status;
      const dmap = {
        sent: "Door unlocked — relay confirmed",
        acked: "Door unlocked — relay confirmed",
        pending: "Door unlocked — gateway queued",
        failed: "Door unlocked — controller unreachable, event logged",
        expired: "Door unlocked — gateway did not respond, event logged",
        no_controller: "Door unlocked — no relay configured",
      };
      const label = dmap[dstatus] || "Door unlocked";
      setNotice(label);
      if (dstatus === "sent" || dstatus === "acked") toast.success(label);
      else if (dstatus === "no_controller" || dstatus === "pending") toast(label);
      else toast.warning(label);
      setTimeout(() => setDoorUnlocked(false), 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    }
  }

  function toggleMute() {
    const stream = localRef.current?.srcObject;
    if (!stream) return;
    stream.getAudioTracks().forEach((t) => { t.enabled = muted; });
    setMuted((m) => !m);
  }
  function toggleVideo() {
    const stream = localRef.current?.srcObject;
    if (!stream) return;
    stream.getVideoTracks().forEach((t) => { t.enabled = videoOff; });
    setVideoOff((v) => !v);
  }

  // Expose live state to Playwright / debugging.
  useEffect(() => {
    window.__fastviCallDebug = {
      role, callId,
      getState: () => ({
        role,
        callId,
        status: statusRef.current,
        iceConnectionState: peerRef.current?.pc?.iceConnectionState || null,
        connectionState: peerRef.current?.pc?.connectionState || null,
        signalingState: peerRef.current?.pc?.signalingState || null,
        wsReady: role === "caller"
          ? wsRef.current?.readyState === 1
          : (getResidentSocket()?.readyState === 1),
        remoteAudioTracks: (remoteRef.current?.srcObject?.getAudioTracks?.() || []).length,
        remoteVideoTracks: (remoteRef.current?.srcObject?.getVideoTracks?.() || []).length,
        localAudioTracks: (localRef.current?.srcObject?.getAudioTracks?.() || []).length,
        localVideoTracks: (localRef.current?.srcObject?.getVideoTracks?.() || []).length,
      }),
      forceIceRestart: () => peerRef.current?.restartIce?.(),
      closeCallerSocket: () => { try { wsRef.current?.close(); } catch { /* noop */ } },
      closeResidentSocket: () => { try { window.__fastviWs?.close(); } catch { /* noop */ } },
    };
    return () => { delete window.__fastviCallDebug; };
  }, [role, callId]);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="call-screen">
      <video ref={remoteRef} autoPlay playsInline
             className="absolute inset-0 w-full h-full object-cover"
             data-testid="remote-video" />
      {!remoteRef.current?.srcObject && (
        <div className="absolute inset-0 grid place-items-center bg-gradient-to-b from-zinc-900 to-black">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.3em] text-white/60 mb-2" data-testid="call-status">
              {status === "ringing" ? "Ringing…" :
               status === "incoming" ? "Incoming call" :
               status === "connected" ? "Connecting video…" :
               status === "rejected" ? (notice || "Call declined") :
               status === "no_answer" ? "No answer" :
               status === "ended" ? "Call ended" : status}
            </div>
            <div className="font-display text-5xl">{visitor}</div>
            {status === "ringing" && (
              <div className="mt-6 inline-block relative">
                <div className="pulse-ring relative size-24 rounded-full bg-accent grid place-items-center">
                  <Phone />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <video ref={localRef} autoPlay muted playsInline
             className="absolute bottom-32 right-6 w-40 sm:w-56 rounded-xl border border-white/20 bg-black/50 z-20"
             data-testid="local-video" />

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none" />

      <div className="absolute top-0 left-0 right-0 p-5 flex items-center justify-between z-30">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-white/60">FastVi Call</div>
          <div className="font-display text-2xl">{visitor}</div>
        </div>
        <Button variant="ghost" className="text-white" onClick={end} data-testid="call-close"><X /></Button>
      </div>

      {notice && (
        <div
          className={`absolute top-20 left-1/2 -translate-x-1/2 z-30 backdrop-blur border rounded-full px-4 py-1.5 text-xs font-mono flex items-center gap-2 ${
            doorUnlocked
              ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-100"
              : "bg-white/10 border-white/20 text-white"
          }`}
          data-testid={doorUnlocked ? "kiosk-door-unlocked" : "call-notice"}
        >
          {doorUnlocked ? <DoorOpen size={12} /> : <WifiOff size={12} />}
          {notice}
        </div>
      )}

      <div
        className="absolute top-4 right-6 z-30 text-[10px] font-mono text-white/50 uppercase tracking-widest"
        data-testid="call-ice-state"
      >
        ice: {iceState} · conn: {connState}
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30">
        <div className="glass-dark rounded-full px-6 py-4 flex items-center gap-4">
          {role === "callee" && status === "incoming" && (
            <>
              <Button onClick={reject} variant="destructive" className="rounded-full size-14" data-testid="call-reject"><PhoneOff /></Button>
              <Button onClick={accept} className="rounded-full size-14 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="call-accept"><Phone /></Button>
            </>
          )}
          {(status === "connected" || status === "ringing") && (
            <>
              <Button variant="ghost" className="rounded-full size-12 text-white hover:bg-white/10" onClick={toggleMute} data-testid="call-mute">{muted ? <MicOff/> : <Mic/>}</Button>
              <Button variant="ghost" className="rounded-full size-12 text-white hover:bg-white/10" onClick={toggleVideo} data-testid="call-video">{videoOff ? <VideoOff/> : <VideoIcon/>}</Button>
              {role === "callee" && status === "connected" && (
                <Button
                  onClick={unlockDoor}
                  className="rounded-full h-12 px-4 bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="call-unlock"
                >
                  <DoorOpen size={16} className="mr-1.5" /> Unlock
                </Button>
              )}
              <Button onClick={end} variant="destructive" className="rounded-full size-14" data-testid="call-end"><PhoneOff /></Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
